import { useState, useRef, useEffect } from "react"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Switch } from "@/components/ui/switch"
import { Slider } from "@/components/ui/slider"
import { GlassCard } from "./GlassCard"
import { AnimatedCard } from "./AnimatedCard"
import { TierBadge } from "./TierBadge"
import { useApexState } from "@/hooks/useApexState"
import { 
  Search, 
  Mic,
  MicOff,
  Filter,
  X,
  Crown,
  ShoppingBag,
  Users,
  Trophy,
  Calendar,
  Star,
  TrendingUp,
  Brain,
  Sparkles,
  History,
  ArrowRight
} from "lucide-react"

interface SearchResult {
  id: string
  type: 'product' | 'user' | 'challenge' | 'event' | 'collection' | 'content'
  title: string
  subtitle?: string
  description?: string
  image?: string
  score: number // relevance score 0-100
  metadata?: {
    price?: number
    tier?: 'initiate' | 'creator' | 'architect'
    isApexLocked?: boolean
    isLimited?: boolean
    category?: string
    tags?: string[]
  }
}

interface SearchSuggestion {
  id: string
  query: string
  type: 'recent' | 'trending' | 'ai_suggested' | 'category'
  count?: number
  confidence?: number
}

interface AdvancedSearchProps {
  isOpen: boolean
  onClose: () => void
}

const mockSuggestions: SearchSuggestion[] = [
  { id: "1", query: "Architect Hoodie", type: "recent" },
  { id: "2", query: "Motion & Form Challenge", type: "recent" },
  { id: "3", query: "kinetic joggers sage", type: "recent" },
  { id: "4", query: "mindful movement", type: "trending", count: 234 },
  { id: "5", query: "urban exploration style", type: "trending", count: 156 },
  { id: "6", query: "creator tier benefits", type: "ai_suggested", confidence: 87 },
  { id: "7", query: "architectural aesthetics", type: "ai_suggested", confidence: 92 },
  { id: "8", query: "training gear", type: "category" },
  { id: "9", query: "lifestyle wear", type: "category" }
]

const mockResults: SearchResult[] = [
  {
    id: "1",
    type: "product",
    title: "Apex Kinetic Joggers",
    subtitle: "$180 • Apex Exclusive",
    description: "Premium joggers designed for seamless movement transitions",
    image: "https://images.unsplash.com/photo-1556905055-8f358a7a47b2?w=200&h=200&fit=crop",
    score: 95,
    metadata: {
      price: 180,
      isApexLocked: true,
      category: "Movement",
      tags: ["kinetic", "joggers", "apex"]
    }
  },
  {
    id: "2",
    type: "user",
    title: "Maya Rodriguez",
    subtitle: "Architect • Movement Expert",
    description: "Featured creator specializing in architectural movement",
    image: "https://images.unsplash.com/photo-1494790108755-2616b67e1c04?w=200&h=200&fit=crop",
    score: 88,
    metadata: {
      tier: "architect",
      category: "Community"
    }
  },
  {
    id: "3",
    type: "challenge",
    title: "Motion & Form Challenge",
    subtitle: "3 days left • 127 participants",
    description: "Style your kinetic pieces in architectural settings",
    score: 92,
    metadata: {
      category: "Challenges",
      tags: ["motion", "form", "architecture"]
    }
  },
  {
    id: "4",
    type: "product",
    title: "Form Training Tank",
    subtitle: "$85 • New Release",
    description: "Minimalist tank perfect for mindful movement practices",
    image: "https://images.unsplash.com/photo-1583900985737-6d0495555783?w=200&h=200&fit=crop",
    score: 76,
    metadata: {
      price: 85,
      category: "Training",
      tags: ["form", "tank", "minimalist"]
    }
  }
]

export function AdvancedSearch({ isOpen, onClose }: AdvancedSearchProps) {
  const { state } = useApexState()
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<SearchResult[]>([])
  const [suggestions] = useState<SearchSuggestion[]>(mockSuggestions)
  const [isVoiceActive, setIsVoiceActive] = useState(false)
  const [showFilters, setShowFilters] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Filter states
  const [filters, setFilters] = useState({
    type: "all",
    priceRange: [0, 500],
    tier: "all",
    category: "all",
    apexOnly: false,
    limitedOnly: false,
    sortBy: "relevance"
  })

  const categories = [
    { id: "all", label: "All Categories", icon: Search },
    { id: "products", label: "Products", icon: ShoppingBag },
    { id: "community", label: "Community", icon: Users },
    { id: "challenges", label: "Challenges", icon: Trophy },
    { id: "events", label: "Events", icon: Calendar }
  ]

  // Simulate search with debouncing
  useEffect(() => {
    if (query.length > 0) {
      setIsLoading(true)
      const timer = setTimeout(() => {
        // Filter and score results based on query
        const filteredResults = mockResults
          .filter(result => 
            result.title.toLowerCase().includes(query.toLowerCase()) ||
            result.description?.toLowerCase().includes(query.toLowerCase()) ||
            result.metadata?.tags?.some(tag => tag.toLowerCase().includes(query.toLowerCase()))
          )
          .sort((a, b) => b.score - a.score)
        
        setResults(filteredResults)
        setIsLoading(false)
      }, 300)

      return () => clearTimeout(timer)
    } else {
      setResults([])
      setIsLoading(false)
    }
  }, [query])

  const startVoiceSearch = () => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      setIsVoiceActive(true)
      // Voice recognition implementation would go here
      setTimeout(() => {
        setIsVoiceActive(false)
        setQuery("kinetic joggers movement")
      }, 2000)
    }
  }

  const getResultIcon = (type: SearchResult['type']) => {
    switch (type) {
      case 'product': return <ShoppingBag size={16} className="text-primary" />
      case 'user': return <Users size={16} className="text-blue-400" />
      case 'challenge': return <Trophy size={16} className="text-accent" />
      case 'event': return <Calendar size={16} className="text-green-400" />
      case 'collection': return <Star size={16} className="text-purple-400" />
      default: return <Search size={16} className="text-muted-foreground" />
    }
  }

  const getSuggestionIcon = (type: SearchSuggestion['type']) => {
    switch (type) {
      case 'recent': return <History size={14} className="text-muted-foreground" />
      case 'trending': return <TrendingUp size={14} className="text-orange-400" />
      case 'ai_suggested': return <Brain size={14} className="text-purple-400" />
      case 'category': return <Filter size={14} className="text-blue-400" />
    }
  }

  const handleSuggestionClick = (suggestion: SearchSuggestion) => {
    setQuery(suggestion.query)
    inputRef.current?.focus()
  }

  const handleResultClick = (result: SearchResult) => {
    console.log('Selected result:', result)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm animate-fade-in">
      <div className="max-w-4xl mx-auto p-4 pt-16">
        <GlassCard blur="lg" opacity="high">
          <div className="p-6">
            {/* Search Header */}
            <div className="flex items-center gap-3 mb-6">
              <div className="flex-1 relative">
                <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground z-10" />
                <Input
                  ref={inputRef}
                  placeholder="Search products, people, challenges..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="pl-10 pr-20 h-12 text-lg bg-input/50 border-white/20"
                  autoFocus
                />
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={startVoiceSearch}
                    className={`p-2 ${isVoiceActive ? 'text-red-400' : 'text-muted-foreground'}`}
                  >
                    {isVoiceActive ? <MicOff size={16} /> : <Mic size={16} />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowFilters(!showFilters)}
                    className="p-2"
                  >
                    <Filter size={16} />
                  </Button>
                </div>
              </div>
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
            </div>

            {/* Voice Search Indicator */}
            {isVoiceActive && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                  <span className="text-red-400 font-medium">Listening...</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Speak your search query clearly
                </p>
              </div>
            )}

            {/* Advanced Filters */}
            {showFilters && (
              <div className="mb-6 p-4 bg-muted/5 border border-white/10 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">
                      Category
                    </label>
                    <select 
                      className="w-full px-3 py-2 bg-input border border-border rounded-lg text-foreground"
                      value={filters.category}
                      onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
                    >
                      <option value="all">All Categories</option>
                      <option value="movement">Movement</option>
                      <option value="lifestyle">Lifestyle</option>
                      <option value="training">Training</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">
                      Price Range
                    </label>
                    <div className="px-2">
                      <Slider
                        value={filters.priceRange}
                        onValueChange={(value) => setFilters(prev => ({ ...prev, priceRange: value }))}
                        min={0}
                        max={500}
                        step={10}
                        className="mb-2"
                      />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>${filters.priceRange[0]}</span>
                        <span>${filters.priceRange[1]}</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-foreground">
                        Apex Exclusive Only
                      </label>
                      <Switch
                        checked={filters.apexOnly}
                        onCheckedChange={(checked) => setFilters(prev => ({ ...prev, apexOnly: checked }))}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-foreground">
                        Limited Edition Only
                      </label>
                      <Switch
                        checked={filters.limitedOnly}
                        onCheckedChange={(checked) => setFilters(prev => ({ ...prev, limitedOnly: checked }))}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Categories */}
            <div className="flex gap-2 mb-6 overflow-x-auto scrollbar-hide">
              {categories.map(category => {
                const Icon = category.icon
                return (
                  <Button
                    key={category.id}
                    variant={filters.type === category.id ? "default" : "outline"}
                    size="sm"
                    onClick={() => setFilters(prev => ({ ...prev, type: category.id }))}
                    className="whitespace-nowrap"
                  >
                    <Icon size={14} className="mr-1" />
                    {category.label}
                  </Button>
                )
              })}
            </div>

            {/* Content */}
            <div className="max-h-96 overflow-y-auto">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : query.length > 0 && results.length > 0 ? (
                <div className="space-y-2">
                  {results.map(result => (
                    <AnimatedCard key={result.id}>
                      <button
                        onClick={() => handleResultClick(result)}
                        className="w-full p-4 text-left hover:bg-white/5 rounded-lg transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          {result.image ? (
                            <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                              <img
                                src={result.image}
                                alt={result.title}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ) : (
                            <div className="w-16 h-16 rounded-lg bg-muted/20 flex items-center justify-center flex-shrink-0">
                              {getResultIcon(result.type)}
                            </div>
                          )}
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-medium text-foreground truncate">
                                {result.title}
                              </h3>
                              {result.metadata?.isApexLocked && (
                                <Crown size={12} className="text-primary flex-shrink-0" />
                              )}
                              {result.metadata?.tier && (
                                <TierBadge tier={result.metadata.tier} size="xs" />
                              )}
                              <Badge variant="outline" className="text-xs flex-shrink-0">
                                {result.type}
                              </Badge>
                            </div>
                            
                            {result.subtitle && (
                              <p className="text-sm text-muted-foreground mb-1">
                                {result.subtitle}
                              </p>
                            )}
                            
                            {result.description && (
                              <p className="text-xs text-muted-foreground truncate">
                                {result.description}
                              </p>
                            )}
                            
                            <div className="flex items-center justify-between mt-2">
                              <div className="flex items-center gap-2">
                                {result.metadata?.tags?.slice(0, 2).map(tag => (
                                  <Badge key={tag} variant="secondary" className="text-xs">
                                    {tag}
                                  </Badge>
                                ))}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {result.score}% match
                              </div>
                            </div>
                          </div>
                          
                          <ArrowRight size={16} className="text-muted-foreground flex-shrink-0" />
                        </div>
                      </button>
                    </AnimatedCard>
                  ))}
                </div>
              ) : query.length > 0 ? (
                <div className="text-center py-8">
                  <Search size={48} className="mx-auto text-muted-foreground opacity-50 mb-4" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    No results found
                  </h3>
                  <p className="text-muted-foreground">
                    Try different keywords or adjust your filters
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* AI Suggestions */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Sparkles size={16} className="text-primary" />
                      <h3 className="font-medium text-foreground">AI Suggestions</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {suggestions.filter(s => s.type === 'ai_suggested').map(suggestion => (
                        <button
                          key={suggestion.id}
                          onClick={() => handleSuggestionClick(suggestion)}
                          className="flex items-center justify-between p-3 rounded-lg bg-gradient-to-r from-purple-500/10 to-primary/10 hover:from-purple-500/20 hover:to-primary/20 border border-purple-500/20 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            {getSuggestionIcon(suggestion.type)}
                            <span className="text-sm text-foreground">{suggestion.query}</span>
                          </div>
                          {suggestion.confidence && (
                            <Badge className="bg-purple-500/20 text-purple-400 text-xs">
                              {suggestion.confidence}%
                            </Badge>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Recent Searches */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <History size={16} className="text-muted-foreground" />
                      <h3 className="font-medium text-foreground">Recent Searches</h3>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {suggestions.filter(s => s.type === 'recent').map(suggestion => (
                        <button
                          key={suggestion.id}
                          onClick={() => handleSuggestionClick(suggestion)}
                          className="flex items-center gap-2 px-3 py-2 rounded-full bg-muted/20 hover:bg-muted/30 transition-colors"
                        >
                          {getSuggestionIcon(suggestion.type)}
                          <span className="text-sm text-foreground">{suggestion.query}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Trending */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <TrendingUp size={16} className="text-orange-400" />
                      <h3 className="font-medium text-foreground">Trending</h3>
                    </div>
                    <div className="space-y-2">
                      {suggestions.filter(s => s.type === 'trending').map(suggestion => (
                        <button
                          key={suggestion.id}
                          onClick={() => handleSuggestionClick(suggestion)}
                          className="flex items-center justify-between w-full p-3 rounded-lg bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/20 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            {getSuggestionIcon(suggestion.type)}
                            <span className="text-sm text-foreground">{suggestion.query}</span>
                          </div>
                          {suggestion.count && (
                            <span className="text-xs text-orange-400">
                              {suggestion.count} searches
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </GlassCard>
      </div>
    </div>
  )
}